"use client"

import { useState } from 'react'
import { motion } from 'framer-motion'
import { FiStar, FiCheckCircle } from 'react-icons/fi'
import { Testimonial } from '@/lib/types'

interface TestimonialCardProps {
  testimonial: Testimonial
}

export function TestimonialCard({ testimonial }: TestimonialCardProps) {
  const [expanded, setExpanded] = useState(false)
  const limit = 160
  const isLong = testimonial.content.length > limit
  const short = isLong ? testimonial.content.slice(0, limit).trimEnd() + '…' : testimonial.content

  return (
    <motion.div
      whileHover={{ y: -5 }}
      className="bg-white rounded-2xl p-8 shadow-lg shadow-slate-200/50 border border-slate-100 h-full"
    >
      {/* Quote Icon */}
      <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-100 to-teal-200 flex items-center justify-center mb-6">
        <span className="text-2xl text-teal-600">"</span>
      </div>

      {/* Rating */}
      <div className="flex items-center gap-1 mb-4">
        {[...Array(5)].map((_, i) => (
          <FiStar
            key={i}
            size={18}
            className={i < testimonial.rating ? 'fill-amber-400 text-amber-400' : 'text-slate-200'}
          />
        ))}
      </div>

      {/* Content (truncated with toggle) */}
      <p className="text-slate-600 mb-3 italic leading-relaxed">"{expanded ? testimonial.content : short}"</p>
      {isLong && (
        <button
          onClick={() => setExpanded((s) => !s)}
          className="text-teal-600 text-sm font-semibold mt-1"
          aria-expanded={expanded}
        >
          {expanded ? 'Show less' : 'Read more'}
        </button>
      )}

      {/* Author */}
      <div className="flex items-center gap-4 pt-6 border-t border-slate-100">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center text-white font-bold text-lg">
          {testimonial.patientName.charAt(0)}
        </div>
        <div>
          <p className="font-semibold text-slate-900 flex items-center gap-2">
            {testimonial.patientName}
            {testimonial.verified && (
              <FiCheckCircle className="w-4 h-4 text-teal-500" />
            )}
          </p>
          {testimonial.patientTitle && (
            <p className="text-sm text-slate-500">{testimonial.patientTitle}</p>
          )}
        </div>
      </div>
    </motion.div>
  )
}
